/**
 * Admin dashboard — every learner, their progress, and at-risk alerts,
 * plus the "create new learner" form. The single screen an admin lands on
 * to see who needs attention and to invite someone new.
 *
 * Self-gating like AdminCapstonesPage: 401 → login redirect, 403 → an
 * "admin access required" notice. At-risk learners are flagged in-app with
 * badges + an "At risk" filter; the nightly digest emails the same set.
 */
import type * as React from "react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";

import { API_BASE_URL, ApiError } from "@/lib/api";
import {
  type AdminSeedData,
  type LearnerListRow,
  type LearnerListResponse,
  archiveLearner,
  createEnrollment,
  getAdminSeedData,
  listLearners,
} from "@/lib/admin-api";
import { ConfirmDialog } from "@/components/ConfirmDialog";

type LoadState =
  | { kind: "loading" }
  | { kind: "ready"; data: LearnerListResponse }
  | { kind: "forbidden" }
  | { kind: "error"; message: string };

type RiskFilter = "all" | "at_risk";

const AT_RISK_LABEL: Record<string, string> = {
  never_signed_in: "Never signed in",
  stalled: "Stalled",
  capstone_awaiting_grading: "Awaiting grading",
  email_bouncing: "Email bouncing",
};

function redirectToLogin(returnTo: string): void {
  window.location.href = `${API_BASE_URL}/api/auth/login?return_to=${encodeURIComponent(returnTo)}`;
}

function riskBadgeClass(reason: string): string {
  return reason === "capstone_awaiting_grading"
    ? "bg-amber-100 text-amber-900"
    : "bg-rose-100 text-rose-900";
}

function statusBadgeClass(status: string): string {
  if (status === "active") return "bg-emerald-100 text-emerald-900";
  if (status === "pending") return "bg-amber-100 text-amber-900";
  return "bg-muted text-foreground";
}

function formatDate(iso: string | null): string {
  return iso ? new Date(iso).toLocaleDateString() : "never";
}

interface PendingArchive {
  userId: string;
  displayName: string;
}

export function AdminDashboardPage(): React.JSX.Element {
  const [load, setLoad] = useState<LoadState>({ kind: "loading" });
  const [filter, setFilter] = useState<RiskFilter>("all");
  const [seed, setSeed] = useState<AdminSeedData | null>(null);
  const [pendingArchive, setPendingArchive] =
    useState<PendingArchive | null>(null);
  const [archiveBusy, setArchiveBusy] = useState(false);
  const [archiveError, setArchiveError] = useState<string | null>(null);

  const refresh = useCallback(async (): Promise<void> => {
    setLoad({ kind: "loading" });
    try {
      const data = await listLearners();
      setLoad({ kind: "ready", data });
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        redirectToLogin("/admin");
        return;
      }
      if (err instanceof ApiError && err.status === 403) {
        setLoad({ kind: "forbidden" });
        return;
      }
      const msg = err instanceof Error ? err.message : "Failed to load learners.";
      setLoad({ kind: "error", message: msg });
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  useEffect(() => {
    // Best-effort: powers the create-learner selects. Failures just leave
    // the form disabled; the main table still renders.
    void getAdminSeedData()
      .then(setSeed)
      .catch(() => setSeed(null));
  }, []);

  const onRequestArchive = useCallback((row: LearnerListRow) => {
    setArchiveError(null);
    setPendingArchive({
      userId: row.user_id,
      displayName: row.name || row.email,
    });
  }, []);

  const onCancelArchive = useCallback(() => {
    if (archiveBusy) return;
    setPendingArchive(null);
  }, [archiveBusy]);

  const onConfirmArchive = useCallback(async (): Promise<void> => {
    if (!pendingArchive) return;
    setArchiveBusy(true);
    setArchiveError(null);
    try {
      await archiveLearner(pendingArchive.userId);
      setPendingArchive(null);
      await refresh();
    } catch (err) {
      setArchiveError(
        err instanceof Error ? err.message : "Could not archive learner.",
      );
    } finally {
      setArchiveBusy(false);
    }
  }, [pendingArchive, refresh]);

  const summary = useMemo(() => {
    if (load.kind !== "ready") return null;
    const rows = load.data.learners;
    return {
      total: load.data.total,
      active: rows.filter((r) => r.enrollment_status === "active").length,
      atRisk: load.data.at_risk_count,
      awaitingGrading: rows.filter((r) => r.capstone_status === "submitted").length,
    };
  }, [load]);

  const visibleRows = useMemo(() => {
    if (load.kind !== "ready") return [];
    return load.data.learners.filter((r) =>
      filter === "at_risk" ? r.at_risk_reasons.length > 0 : true,
    );
  }, [load, filter]);

  if (load.kind === "forbidden") {
    return (
      <div className="mx-auto max-w-2xl px-4 py-12 sm:px-6">
        <h1 className="text-2xl font-semibold text-foreground">Admin access required</h1>
        <p className="mt-4 text-muted-foreground">
          The learner dashboard is only available to accounts with the admin role. Sign in
          with an admin account to continue.
        </p>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-6xl px-4 py-8 sm:px-6">
      <header className="flex items-center justify-between border-b border-border pb-6">
        <div>
          <h1 className="text-2xl font-semibold text-foreground">Learner dashboard</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Every learner, their progress, and who isn&rsquo;t moving. Admins also get a
            nightly email digest of at-risk learners.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Link
            to="/admin/handbook"
            className="rounded-md border border-border px-3 py-1.5 text-sm font-medium text-foreground hover:bg-muted"
          >
            Handbook
          </Link>
          <Link
            to="/admin/learners/archived"
            className="rounded-md border border-border px-3 py-1.5 text-sm font-medium text-foreground hover:bg-muted"
          >
            Archive
          </Link>
          <Link
            to="/admin/capstones"
            className="rounded-md border border-border px-3 py-1.5 text-sm font-medium text-foreground hover:bg-muted"
          >
            Grading queue →
          </Link>
        </div>
      </header>

      {summary ? (
        <section className="mt-6 grid grid-cols-2 gap-4 sm:grid-cols-4">
          <SummaryCard label="Learners" value={summary.total} />
          <SummaryCard label="Active" value={summary.active} />
          <SummaryCard label="At risk" value={summary.atRisk} tone="rose" />
          <SummaryCard label="Awaiting grading" value={summary.awaitingGrading} tone="amber" />
        </section>
      ) : null}

      <CreateLearnerForm seed={seed} onCreated={refresh} />

      <div className="mt-8 flex items-center justify-between">
        <h2 className="text-lg font-semibold text-foreground">Learners</h2>
        <div className="flex gap-2">
          {(["all", "at_risk"] as RiskFilter[]).map((key) => (
            <button
              key={key}
              type="button"
              onClick={() => setFilter(key)}
              className={
                filter === key
                  ? "rounded-md border border-primary bg-primary px-3 py-1.5 text-sm font-medium text-white"
                  : "rounded-md border border-border px-3 py-1.5 text-sm font-medium text-foreground hover:bg-muted"
              }
            >
              {key === "all" ? "All" : "At risk"}
            </button>
          ))}
        </div>
      </div>

      <section className="mt-4 overflow-x-auto rounded-lg border border-border bg-white shadow-sm">
        {load.kind === "loading" ? (
          <p className="px-6 py-12 text-center text-muted-foreground">Loading…</p>
        ) : load.kind === "error" ? (
          <p className="px-6 py-12 text-center text-rose-700">{load.message}</p>
        ) : visibleRows.length === 0 ? (
          <p className="px-6 py-12 text-center text-muted-foreground">
            {filter === "at_risk" ? "No learners are flagged. 🎉" : "No learners yet."}
          </p>
        ) : (
          <table className="min-w-full divide-y divide-border">
            <thead className="bg-background">
              <tr>
                {[
                  "Learner",
                  "Status",
                  "Lessons",
                  "Modules",
                  "Exam",
                  "Capstone",
                  "Cert",
                  "Badges",
                  "XP",
                  "Last active",
                  "Flags",
                ].map((h) => (
                  <th
                    key={h}
                    className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground"
                  >
                    {h}
                  </th>
                ))}
                {/* Sticky action column — pinned to the right edge so
                    Archive stays visible no matter how far the table
                    horizontally scrolls on narrower viewports. */}
                <th
                  scope="col"
                  className="sticky right-0 z-10 bg-background px-4 py-2 text-right text-xs font-semibold uppercase tracking-wider text-muted-foreground shadow-[inset_1px_0_0_theme(colors.border)]"
                >
                  <span className="sr-only">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {visibleRows.map((row) => (
                <LearnerRow
                  key={row.user_id}
                  row={row}
                  onArchive={onRequestArchive}
                />
              ))}
            </tbody>
          </table>
        )}
      </section>

      {archiveError ? (
        <p className="mt-3 text-sm text-rose-700" role="alert">
          {archiveError}
        </p>
      ) : null}

      <ConfirmDialog
        open={pendingArchive !== null}
        title="Archive this learner?"
        description={
          pendingArchive ? (
            <span>
              <strong>{pendingArchive.displayName}</strong> will be hidden from
              the active dashboard and the weekly at-risk digest. Their
              enrollment, progress, certificate, and community profile are
              <em> not</em> deleted — you can restore them from the Archive
              page at any time.
            </span>
          ) : (
            ""
          )
        }
        confirmLabel="Archive learner"
        confirmTone="danger"
        busy={archiveBusy}
        onConfirm={onConfirmArchive}
        onCancel={onCancelArchive}
      />
    </div>
  );
}

interface SummaryCardProps {
  label: string;
  value: number;
  tone?: "rose" | "amber";
}

function SummaryCard({ label, value, tone }: SummaryCardProps): React.JSX.Element {
  const valueClass =
    tone === "rose" && value > 0
      ? "text-rose-700"
      : tone === "amber" && value > 0
        ? "text-amber-700"
        : "text-foreground";
  return (
    <div className="rounded-lg border border-border bg-white p-4 shadow-sm">
      <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
        {label}
      </div>
      <div className={`mt-1 text-2xl font-semibold ${valueClass}`}>{value}</div>
    </div>
  );
}

interface LearnerRowProps {
  row: LearnerListRow;
  onArchive: (row: LearnerListRow) => void;
}

function LearnerRow({ row, onArchive }: LearnerRowProps): React.JSX.Element {
  return (
    <tr>
      <td className="px-4 py-3">
        <Link
          to={`/admin/learners/${row.user_id}`}
          className="font-medium text-foreground underline-offset-2 hover:underline"
        >
          {row.name || row.email}
        </Link>
        <div className="text-xs text-muted-foreground">{row.email}</div>
      </td>
      <td className="px-4 py-3 text-sm">
        <span
          className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${statusBadgeClass(row.enrollment_status)}`}
        >
          {row.enrollment_status}
        </span>
        {!row.signed_in ? (
          <span className="ml-1 text-xs text-muted-foreground">· not signed in</span>
        ) : null}
      </td>
      <td className="px-4 py-3 text-sm text-foreground">
        {row.lessons_complete}/{row.lessons_total}
      </td>
      <td className="px-4 py-3 text-sm text-foreground">
        {row.modules_passed}/{row.modules_total}
      </td>
      <td className="px-4 py-3 text-sm text-foreground">{row.exam_status.replaceAll("_", " ")}</td>
      <td className="px-4 py-3 text-sm text-foreground">
        {row.capstone_status.replaceAll("_", " ")}
      </td>
      <td className="px-4 py-3 text-sm text-foreground">{row.cert_issued ? "✓" : "—"}</td>
      <td className="px-4 py-3 text-sm text-foreground">{row.badge_count ?? 0}</td>
      <td className="px-4 py-3 text-sm text-foreground">{row.total_xp ?? 0}</td>
      <td className="px-4 py-3 text-sm text-muted-foreground">{formatDate(row.last_activity_at)}</td>
      <td className="px-4 py-3">
        <div className="flex flex-wrap gap-1">
          {row.at_risk_reasons.map((reason) => (
            <span
              key={reason}
              className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${riskBadgeClass(reason)}`}
            >
              {AT_RISK_LABEL[reason] ?? reason}
            </span>
          ))}
        </div>
      </td>
      <td className="sticky right-0 z-10 bg-white px-4 py-3 text-right shadow-[inset_1px_0_0_theme(colors.border)]">
        <button
          type="button"
          onClick={() => onArchive(row)}
          className="rounded-md border border-rose-300 bg-white px-2.5 py-1 text-xs font-medium text-rose-700 hover:bg-rose-50"
          aria-label={`Archive ${row.name || row.email}`}
        >
          Archive
        </button>
      </td>
    </tr>
  );
}

interface CreateLearnerFormProps {
  seed: AdminSeedData | null;
  onCreated: () => void | Promise<void>;
}

function CreateLearnerForm({ seed, onCreated }: CreateLearnerFormProps): React.JSX.Element {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [courseVersionId, setCourseVersionId] = useState("");
  const [priceTierId, setPriceTierId] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [banner, setBanner] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (seed) {
      setCourseVersionId((prev) => prev || seed.active_course_versions[0]?.id || "");
      setPriceTierId((prev) => prev || seed.active_price_tiers[0]?.id || "");
    }
  }, [seed]);

  const canSubmit =
    !submitting && email.trim() !== "" && courseVersionId !== "" && priceTierId !== "";

  const onSubmit = useCallback(async (): Promise<void> => {
    setSubmitting(true);
    setError(null);
    setBanner(null);
    try {
      const body =
        name.trim() === ""
          ? { email: email.trim(), course_version_id: courseVersionId, price_tier_id: priceTierId }
          : {
              email: email.trim(),
              name: name.trim(),
              course_version_id: courseVersionId,
              price_tier_id: priceTierId,
            };
      const result = await createEnrollment(body);
      setBanner(`Invited ${email.trim()} (${result.status}). ${result.message}`);
      setEmail("");
      setName("");
      await onCreated();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not create the learner.");
    } finally {
      setSubmitting(false);
    }
  }, [email, name, courseVersionId, priceTierId, onCreated]);

  return (
    <section className="mt-6 rounded-lg border border-border bg-white p-4 shadow-sm sm:p-6">
      <h2 className="text-sm font-semibold text-foreground">Create a new learner</h2>
      <p className="mt-1 text-xs text-muted-foreground">
        Pre-provisions a pending enrollment and emails a welcome. The learner activates on
        first sign-in.
      </p>
      <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
        <label className="block">
          <span className="text-xs font-medium text-foreground">Email</span>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="clinician@example.com"
            className="mt-1 w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground placeholder-muted-foreground focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
          />
        </label>
        <label className="block">
          <span className="text-xs font-medium text-foreground">Name (optional)</span>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Jane Doe"
            className="mt-1 w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground placeholder-muted-foreground focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
          />
        </label>
        <label className="block">
          <span className="text-xs font-medium text-foreground">Course version</span>
          <select
            value={courseVersionId}
            onChange={(e) => setCourseVersionId(e.target.value)}
            disabled={!seed}
            className="mt-1 w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary disabled:opacity-60"
          >
            {seed?.active_course_versions.map((cv) => (
              <option key={cv.id} value={cv.id}>
                {cv.course_title} v{cv.version}
              </option>
            ))}
          </select>
        </label>
        <label className="block">
          <span className="text-xs font-medium text-foreground">Price tier</span>
          <select
            value={priceTierId}
            onChange={(e) => setPriceTierId(e.target.value)}
            disabled={!seed}
            className="mt-1 w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary disabled:opacity-60"
          >
            {seed?.active_price_tiers.map((pt) => (
              <option key={pt.id} value={pt.id}>
                {pt.label}
              </option>
            ))}
          </select>
        </label>
      </div>

      {banner ? (
        <div className="mt-3 rounded-md border border-emerald-300 bg-emerald-50 px-4 py-2 text-sm text-emerald-900">
          {banner}
        </div>
      ) : null}
      {error ? (
        <div className="mt-3 rounded-md border border-rose-300 bg-rose-50 px-4 py-2 text-sm text-rose-900">
          {error}
        </div>
      ) : null}

      <div className="mt-4">
        <button
          type="button"
          onClick={() => {
            void onSubmit();
          }}
          disabled={!canSubmit}
          className="rounded-md border border-primary bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-primary/90 disabled:opacity-60"
        >
          {submitting ? "Creating…" : "Create learner"}
        </button>
      </div>
    </section>
  );
}
